<script>
import { GlTooltipDirective } from '@gitlab/ui';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { calculateBlameOffset } from '../utils';
import { BLAME_AGE_COLORS, BLAME_COLUMN_DEFAULT_WIDTH } from '../constants';
import BlameCommitInfo from './blame_commit_info.vue';
import BlameColumnResizer from './blame_column_resizer.vue';

export default {
  name: 'BlameInfo',
  BLAME_AGE_COLORS,
  components: {
    BlameCommitInfo,
    BlameColumnResizer,
  },
  directives: {
    GlTooltip: GlTooltipDirective,
    SafeHtml,
  },
  props: {
    blameInfo: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      containerHeight: 0,
      containerWidth: BLAME_COLUMN_DEFAULT_WIDTH,
    };
  },
  computed: {
    processedBlameInfo() {
      return this.blameInfo.map((blame, index) => {
        const ageClass = blame.commitData?.ageMapClass ?? '';
        const indicatorColor = this.$options.BLAME_AGE_COLORS[ageClass] || 'transparent';

        const height = this.calculateCommitHeight(blame, this.blameInfo[index + 1]);

        return {
          ...blame,
          style: {
            '--blame-indicator-top': blame.blameOffset,
            '--blame-indicator-color': indicatorColor,
            '--blame-indicator-height': `${height}px`,
          },
        };
      });
    },
  },
  watch: {
    blameInfo: {
      handler() {
        this.$nextTick(() => {
          this.updateContainerHeight();
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.updateContainerHeight();
  },
  methods: {
    calculateCommitHeight(commitInfo, nextCommitInfo) {
      const currentOffset = parseInt(commitInfo.blameOffset, 10) || 0;
      const { lineno, span } = commitInfo;

      // Bound the indicator to this group's range. If its end line is in an
      // unloaded chunk, render nothing rather than spanning across the gap.
      if (span) {
        const groupEndOffset = calculateBlameOffset(lineno + span);
        if (groupEndOffset === null) return 0;
        return Math.max(parseInt(groupEndOffset, 10) - currentOffset, 0);
      }

      if (!nextCommitInfo) return this.containerHeight - currentOffset;

      const nextOffset = parseInt(nextCommitInfo.blameOffset, 10) || 0;
      return Math.max(nextOffset - currentOffset, 0);
    },
    updateContainerHeight() {
      if (this.$el) {
        this.containerHeight = this.$el.offsetHeight;
      }
    },
  },
};
</script>
<template>
  <div class="blame gl-border-r gl-relative gl-bg-subtle" :style="{ width: `${containerWidth}px` }">
    <blame-column-resizer v-model="containerWidth" />

    <div class="blame-commit !gl-border-none">
      <template v-if="blameInfo.length">
        <span
          v-for="(processedBlame, index) in processedBlameInfo"
          :key="`indicator-${index}`"
          :ref="`indicator-${index}`"
          class="blame-commit-wrapper"
          :style="processedBlame.style"
          aria-hidden="true"
        ></span>
      </template>

      <template v-if="blameInfo.length">
        <blame-commit-info
          v-for="(blame, index) in blameInfo"
          :key="index"
          :class="{ 'gl-border-t': blame.blameOffset !== '0px' }"
          class="gl-absolute gl-flex gl-px-3"
          :style="{ top: blame.blameOffset }"
          :commit="blame.commit"
          :span="blame.span"
          :previous-blame-path="blame.commitData && blame.commitData.previousBlamePath"
        />
      </template>
    </div>
  </div>
</template>

<style scoped>
.blame-commit-wrapper::before {
  content: '';
  position: absolute;
  left: 0;
  top: var(--blame-indicator-top);
  height: var(--blame-indicator-height);
  width: 3px;
  background-color: var(--blame-indicator-color);
  pointer-events: none;
}
</style>
